import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Rocket, Server, MessageSquare, Phone, CreditCard, LayoutDashboard, Loader2, Zap, ArrowLeft, Home } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { BackButton } from "@/components/layout/BackButton";
import { useEffect } from "react";
import { BuildConfiguratorSEO } from "@/components/SEO";
import { MainContent } from "@/components/MainContent";

interface Module {
  id: string;
  label: string;
  description: string;
  price: number;
  icon: any;
}

const modules: Module[] = [
  { id: "chatbot", label: "Custom AI Chatbot", description: "24/7 conversational agent trained on your business data with embed instructions.", price: 500, icon: MessageSquare },
  { id: "workflow", label: "Workflow Automation", description: "Make/Zapier/n8n recipes, workflow diagrams, and integration setup guides.", price: 750, icon: Zap },
  { id: "compliance", label: "Compliance & Policy Pack", description: "Terms, Privacy, Refund policies, and claims-safe marketing copy.", price: 399, icon: Server },
  { id: "conversion", label: "Website & Conversion Tune-Up", description: "Landing page copy, CTAs, SEO plan, FAQ, and do/don't claim guidelines.", price: 599, icon: LayoutDashboard },
  { id: "website", label: "AI Website Builder", description: "High-performance landing page with AI-generated copy.", price: 997, icon: LayoutDashboard },
  { id: "ai_agent", label: "Business Agent", description: "Email/SMS follow-up automation with CRM integration.", price: 1000, icon: Rocket },
];

export default function BuildConfigurator() {
  const { toast } = useToast();
  const [selectedModules, setSelectedModules] = useState<string[]>(["chatbot", "workflow"]);
  const [spec, setSpec] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [quote, setQuote] = useState<{ totalCents: number; breakdown: any[]; buildId: number } | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string>("");
  const [availablePlans, setAvailablePlans] = useState<Array<{id: string, name: string, price: number}>>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const toggleModule = (id: string) => {
    setSelectedModules(prev => 
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const generateQuote = async () => {
    if (!spec.trim()) {
      toast({
        title: "Missing Details",
        description: "Please describe your project needs before generating a quote.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    
    try {
      const fullSpec = `${spec}\n\nSelected modules: ${selectedModules.join(", ")}\n${isUrgent ? "URGENT: Rush delivery needed" : ""}\n${isEnterprise ? "AI_OPERATIONS: Full operations setup with monitoring and integrations required" : ""}`;

      const response = await fetch("/api/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ spec: fullSpec }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate quote");
      }

      const data = await response.json();
      setQuote(data);
      
      toast({
        title: "Quote Generated",
        description: "Your custom build estimate is ready.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate quote. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCheckout = async () => {
    if (!quote?.buildId) return;

    setIsCheckingOut(true);

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ buildId: quote.buildId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        
        // Show more detailed error message to users
        let errorMessage = errorData.error || "Failed to create checkout session";
        if (errorData.details) {
          console.error("Checkout error details:", errorData.details);
          
          // If it's a configuration error, mention it
          if (errorData.details.includes("API key") || errorData.details.includes("not set")) {
            errorMessage = "Payment system not configured. Please contact support.";
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      toast({
        title: "Payment Error",
        description: error.message || "Failed to initiate checkout. Please try again or contact support.",
        variant: "destructive",
      });
      setIsCheckingOut(false);
    }
  };

  // Fetch available subscription plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const response = await fetch("/api/services");
        if (response.ok) {
          const data = await response.json();
          // Map services to plan format if available
          if (data.services && Array.isArray(data.services)) {
            const plans = data.services
              .filter((s: any) => s.enabled && s.basePriceCents > 0)
              .map((s: any) => ({
                id: `price_${s.id}`,
                name: s.name,
                price: s.basePriceCents
              }));
            setAvailablePlans(plans);
          }
        }
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleStripeCheckout = async () => {
    if (!selectedPriceId) {
      toast({
        title: "No Price Selected",
        description: "Please select a subscription plan before proceeding to checkout.",
        variant: "destructive",
      });
      return;
    }

    setIsCheckingOut(true);

    try {
      const response = await fetch("/api/stripe/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId: selectedPriceId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        
        // Show more detailed error message to users
        let errorMessage = errorData.error || "Failed to create checkout session";
        if (errorData.details) {
          // Include details for debugging
          console.error("Stripe error details:", errorData.details);
          
          // If it's a configuration error, mention it
          if (errorData.details.includes("API key") || errorData.details.includes("not set")) {
            errorMessage = "Payment system not configured. Please contact support.";
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      toast({
        title: "Payment Error",
        description: error.message || "Failed to initiate checkout. Please try again or contact support.",
        variant: "destructive",
      });
      setIsCheckingOut(false);
    }
  };

  return (
    <>
      <BuildConfiguratorSEO />
      <MainContent className="min-h-screen bg-background pt-8 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <BackButton fallback="/" />
          </div>
        </div>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
        
        <div>
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-400">
              System Configurator
            </h1>
            <p className="text-muted-foreground text-lg">
              Select the components for your AI infrastructure. Our engine will generate an instant fixed-price quote.
            </p>
          </div>

          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {modules.map((module) => {
                const isSelected = selectedModules.includes(module.id);
                return (
                  <motion.div
                    key={module.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => toggleModule(module.id)}
                    className={`
                      cursor-pointer p-4 rounded-xl border transition-all duration-200
                      ${isSelected 
                        ? 'bg-primary/10 border-primary shadow-[0_0_15px_rgba(124,58,237,0.2)]' 
                        : 'bg-card/50 border-white/5 hover:border-white/20'}
                    `}
                    data-testid={`module-${module.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <module.icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      {isSelected && <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
                    </div>
                    <h3 className={`font-bold ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {module.label}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">{module.description}</p>
                    <div className="mt-3 text-sm font-mono opacity-70">
                      ${module.price}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <Card className="bg-card/30 border-white/10">
              <CardHeader>
                <CardTitle className="text-lg">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Describe your specific needs</Label>
                  <Textarea 
                    placeholder="e.g. I need the chatbot to handle real estate inquiries and schedule viewings..."
                    className="min-h-[100px] bg-background/50"
                    value={spec}
                    onChange={(e) => setSpec(e.target.value)}
                    data-testid="input-spec"
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-6 pt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="urgent" 
                      checked={isUrgent}
                      onCheckedChange={(c) => setIsUrgent(!!c)}
                      data-testid="checkbox-urgent"
                    />
                    <Label htmlFor="urgent" className="cursor-pointer">Rush Delivery (+25%)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="enterprise" 
                      checked={isEnterprise}
                      onCheckedChange={(c) => setIsEnterprise(!!c)}
                      data-testid="checkbox-enterprise"
                    />
                    <Label htmlFor="enterprise" className="cursor-pointer">AI Operations Setup (+35%)</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button 
              size="lg" 
              className="w-full text-lg h-14 bg-primary hover:bg-primary/90 shadow-[0_0_30px_rgba(124,58,237,0.4)]"
              onClick={generateQuote}
              disabled={isGenerating}
              data-testid="button-generate-quote"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Analyzing Requirements...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-5 w-5" />
                  Generate Quote
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 bg-primary/5 blur-3xl rounded-full -z-10 transform translate-x-1/2" />
          
          <Card className="h-full border-primary/20 bg-card/80 backdrop-blur-xl shadow-2xl sticky top-24">
            <CardHeader className="border-b border-white/5 bg-primary/5">
              <div className="flex items-center justify-between">
                <CardTitle>System Estimate</CardTitle>
                <Badge variant="outline" className="font-mono" data-testid="badge-build-id">ID: {quote?.buildId || "—"}</Badge>
              </div>
              <CardDescription>Valid for 7 days</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {quote ? (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                  data-testid="quote-result"
                >
                  <div className="space-y-3">
                    {quote.breakdown.map((item: any, i: number) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-mono">${(item.cents / 100).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-4 bg-background/50 rounded-lg border border-white/5">
                    <div className="flex justify-between items-end">
                      <span className="text-lg font-medium">Total Investment</span>
                      <span className="text-3xl font-bold text-primary font-mono" data-testid="text-total">
                        ${(quote.totalCents / 100).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="price-select">Select Subscription Plan (Optional)</Label>
                      <Select value={selectedPriceId} onValueChange={setSelectedPriceId} disabled={plansLoading}>
                        <SelectTrigger id="price-select" className="w-full">
                          <SelectValue placeholder={plansLoading ? "Loading plans..." : "Choose a plan..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePlans.length > 0 ? (
                            availablePlans.map((plan) => (
                              <SelectItem key={plan.id} value={plan.id}>
                                {plan.name} - ${(plan.price / 100).toLocaleString()}/month
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="" disabled>
                              No subscription plans available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {availablePlans.length === 0 && !plansLoading && (
                        <p className="text-xs text-muted-foreground">
                          Subscription plans will be configured by your administrator.
                        </p>
                      )}
                    </div>

                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold h-12"
                      onClick={handleCheckout}
                      disabled={isCheckingOut}
                      data-testid="button-checkout"
                    >
                      {isCheckingOut ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Proceed to Payment"
                      )}
                    </Button>

                    {selectedPriceId && (
                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12"
                        onClick={handleStripeCheckout}
                        disabled={isCheckingOut}
                        data-testid="button-stripe-checkout"
                      >
                        {isCheckingOut ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-4 w-4" />
                            Subscribe with Stripe
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </motion.div>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-muted-foreground opacity-50 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                    <LayoutDashboard className="w-8 h-8" />
                  </div>
                  <p>Configure your system to view pricing</p>
                </div>
              )}
            </CardContent>
            {quote && (
              <CardFooter className="bg-primary/5 border-t border-white/5 p-4">
                <p className="text-xs text-muted-foreground text-center w-full">
                  * Final deliverables include full source code transfer and 30 days of support.
                </p>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </MainContent>
    </>
  );
}
